import React from "react";
import {Box, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Panel} from "./Panel";

// One pastel per meaning, so a metric looks the same on every page
export const STAT_TONES = {
  lavender: {bg: "#EEF0FE", color: "#8491F7"},
  mint: {bg: "#E8F5EE", color: "#4FAE86"},
  sand: {bg: "#FBF1DF", color: "#D9A13A"},
  sky: {bg: "#E6F0FA", color: "#6BA3D6"},
};

// Summary tile: pastel icon badge, label, big number and an optional note. meter (0-1) adds a thin bar in the
// tone's colour for stats that are a share of something. badge ({label, bg, color}) is a status pill next to the number.
export const StatTile = ({icon, tone = "lavender", label, value, note, meter, badge}) => {
  const {bg, color} = STAT_TONES[tone] || STAT_TONES.lavender;
  const fill = meter === null || meter === undefined ? null : Math.max(0, Math.min(1, Number(meter) || 0));

  return (
    <Panel sx={{p: 2, flexDirection: "row", alignItems: "center", gap: 1.5}}>
      <Box sx={{
        width: 40, height: 40, borderRadius: "12px", flexShrink: 0, display: "flex", alignItems: "center",
        justifyContent: "center", color, bgcolor: bg,
      }}>
        <HugeiconsIcon icon={icon} size={19}/>
      </Box>
      <Box sx={{minWidth: 0, flex: 1}}>
        <Typography noWrap sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8A8AA0"}}>
          {label}
        </Typography>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <Typography sx={{fontSize: 24, fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.02em", color: "#23264D"}}>
            {value}
          </Typography>
          {badge && (
            <Box component={"span"} sx={{
              px: 1, py: 0.25, borderRadius: "999px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
              bgcolor: badge.bg, color: badge.color,
            }}>
              {badge.label}
            </Box>
          )}
        </Stack>
        {fill !== null && (
          <Box sx={{height: 4, borderRadius: "999px", bgcolor: "#F2F2F7", overflow: "hidden", mt: 0.75, mb: 0.25}}>
            <Box sx={{height: "100%", width: `${fill * 100}%`, borderRadius: "999px", bgcolor: color, transition: "width .4s ease"}}/>
          </Box>
        )}
        {note && <Typography noWrap sx={{fontSize: 12, color: "#8A8AA0", mt: fill === null ? 0 : 0.25}}>{note}</Typography>}
      </Box>
    </Panel>
  );
};

// Summary cards: separate white cards with a soft shadow, no wrapper around them.
// items: [{label, value, note, meter}]. meter (0-1) adds a thin bar for stats that are a share of something.
export const StatStrip = ({items}) => (
  <Box sx={{
    display: "grid", gap: 2,
    gridTemplateColumns: {xs: "repeat(2, minmax(0, 1fr))", lg: `repeat(${items.length}, minmax(0, 1fr))`},
  }}>
    {items.map(({label, value, note, meter}, index) => {
      const fill = meter === null || meter === undefined ? null : Math.max(0, Math.min(1, Number(meter) || 0));
      return (
        <Box key={label} sx={{
          p: {xs: 1.5, md: 2.25}, borderRadius: "16px", bgcolor: "#fff", minWidth: 0,
          ...(items.length % 2 === 1 && index === items.length - 1 && {gridColumn: {xs: "1 / -1", lg: "auto"}}),
          boxShadow: "0 2px 12px rgba(35, 38, 77, 0.07)",
        }}>
          <Typography noWrap sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8491F7"}}>
            {label}
          </Typography>
          <Typography sx={{mt: 0.5, fontSize: {xs: 24, md: 28}, fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.02em", color: "#23264D"}}>
            {value}
          </Typography>
          {fill !== null && (
            <Box sx={{height: 4, borderRadius: "999px", bgcolor: "#ECECF3", overflow: "hidden", mt: 1}}>
              <Box sx={{height: "100%", width: `${fill * 100}%`, borderRadius: "999px", bgcolor: "#8491F7", transition: "width .4s ease"}}/>
            </Box>
          )}
          {note && <Typography sx={{mt: 0.75, fontSize: 12, color: "#8A8AA0"}}>{note}</Typography>}
        </Box>
      );
    })}
  </Box>
);
